import { useEffect, useMemo } from 'react';

import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useDispatch } from 'react-redux';

import { trackAssortmentTestEntered } from '~/shared/actions';
import { useLDFlags } from '~/shared/utils/useLDFlags';

import { useConfigValue } from './config';

const SESSION_KEY_PREFIX = 'assortment_test_fired:';

// In-memory cache populated from sessionStorage on module load.
// Prevents redundant storage reads on re-renders while still surviving hard reloads.
function loadFiredTestsFromSession(): Set<string> {
  try {
    return new Set(
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith(SESSION_KEY_PREFIX))
        .map((k) => k.slice(SESSION_KEY_PREFIX.length))
    );
  } catch {
    return new Set();
  }
}

const firedTests =
  typeof sessionStorage !== 'undefined'
    ? loadFiredTestsFromSession()
    : new Set<string>();

function markTestFired(testName: string) {
  firedTests.add(testName);
  try {
    sessionStorage.setItem(`${SESSION_KEY_PREFIX}${testName}`, '1');
  } catch {
    // sessionStorage unavailable (private browsing, quota exceeded, etc.)
  }
}

/**
 * Fires an "Assortment Test Entered" Segment event for each active assortment test
 * where the LaunchDarkly flag evaluation confirms the user is in the experiment
 * (`reason.inExperiment === true`). Safe to call from multiple components — deduplication
 * is handled via sessionStorage so each test name fires at most once per browser session.
 */
export function useAssortmentTestTracking(activeAssortmentTests?: string[]) {
  const ldClient = useLDClient();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!activeAssortmentTests?.length || !ldClient) {
      return;
    }

    for (const testName of activeAssortmentTests) {
      if (firedTests.has(testName)) {
        continue;
      }

      const detail = ldClient.variationDetail(testName);

      if (detail?.reason?.inExperiment) {
        markTestFired(testName);
        dispatch(
          trackAssortmentTestEntered(
            { testName, variation: detail.variationIndex },
            undefined
          )
        );
      }
    }
  }, [activeAssortmentTests, ldClient, dispatch]);
}

/**
 * This hook consolidates active assortment test configurations
 * from LaunchDarkly flags and returns merged exclusion lists.
 *
 * ### Behavior
 * - Reads the list of active assortment tests from Builder private config (`public.brand.assortmentTests`).
 * - Retrieves LaunchDarkly feature flag values via `useLDFlags()`.
 * - For each active test, merges its `excludeTagIds` and `excludeFplCodes`
 *   arrays into unified, deduplicated lists.
 *
 * ### Parameters (getAssortmentTestConfigs)
 * - `activeAssortmentTests`: Array of test names to check (from Builder config).
 * - `ldFlags`: LaunchDarkly flags object containing test configurations.
 *
 * ### Returns
 * An object with:
 * {
 *   excludeTagIds: number[];   // Unique tag IDs to exclude across all active tests
 *   excludeFplCodes: string[]; // Unique FPL codes to exclude across all active tests
 * }
 *
 * // As a pure function (for getInitialProps or SSR)
 * const { excludeTagIds, excludeFplCodes } = getAssortmentTestConfigs(
 *   activeAssortmentTests,
 *   ldFlags
 * );
 * ```
 *
 * ### Integration Points
 * - **PDP Query**: Pass exclusions to `useProductQueryAndPreload` to filter related products.
 */
export function getAssortmentTestConfigs(
  activeAssortmentTests?: string[],
  ldFlags?: any
) {
  if (!activeAssortmentTests?.length) {
    return { excludeFplCodes: [], excludeTagIds: [] };
  }

  const excludeTagIds = new Set<number>();
  const excludeFplCodes = new Set<string>();

  for (const test of activeAssortmentTests) {
    const flag = ldFlags[test];
    if (!flag) {
      continue;
    }

    if (Array.isArray(flag.excludeTagIds)) {
      for (const id of flag.excludeTagIds) {
        excludeTagIds.add(id);
      }
    }
    if (Array.isArray(flag.excludeFplCodes)) {
      for (const code of flag.excludeFplCodes) {
        excludeFplCodes.add(code);
      }
    }
  }

  return {
    excludeTagIds: Array.from(excludeTagIds),
    excludeFplCodes: Array.from(excludeFplCodes),
  };
}

export default function useAssortmentTestConfigs() {
  const activeAssortmentTests = useConfigValue(
    'public.brand.assortmentTests'
  ) as string[] | undefined;
  const ldFlags = useLDFlags();

  useAssortmentTestTracking(activeAssortmentTests);

  // Memoize to prevent new array references on every render
  return useMemo(() => {
    return getAssortmentTestConfigs(activeAssortmentTests, ldFlags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only recompute when the stringified values actually change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(activeAssortmentTests),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(ldFlags),
  ]);
}
